import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Wind } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ChatMessage({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed" style={{
        background: isUser ? 'var(--aqua)' : 'rgba(255,255,255,0.04)',
        color: isUser ? 'white' : 'var(--text-secondary-light)',
        borderBottomRightRadius: isUser ? 4 : 16,
        borderBottomLeftRadius: isUser ? 16 : 4,
      }}>
        {msg.content}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="px-4 py-3 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
        <div className="flex gap-1">
          {[0, 1, 2].map(d => <div key={d} className="w-2 h-2 rounded-full" style={{ background: 'var(--aqua)', animation: `breathe 1s ease-in-out ${d * 0.2}s infinite` }} />)}
        </div>
      </div>
    </div>
  );
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: "Hi! I'm Vayu Assistant. How can I help you learn about our smart air quality systems?" }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch (err) {
      logger.error('Chat error:', err.message);
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't connect. Please try again or call us at +91 91 3758 9191." }]);
    }
    setLoading(false);
  }, [input, loading, sessionId]);

  const handleKeyDown = useCallback((e) => { if (e.key === 'Enter') handleSend(); }, [handleSend]);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110" style={{ background: 'var(--aqua)', boxShadow: '0 0 30px rgba(63,167,214,0.3)' }} data-testid="chatbot-toggle">
        <MessageCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[90] w-[360px] max-h-[520px] rounded-2xl flex flex-col overflow-hidden" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-dark)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} data-testid="chatbot-panel">
      <div className="flex items-center justify-between px-5 py-3" style={{ background: 'var(--bg-deep)', borderBottom: '1px solid var(--border-dark)' }}>
        <div className="flex items-center gap-2">
          <Wind className="w-4 h-4" style={{ color: 'var(--aqua)' }} strokeWidth={1.5} />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Inter', color: 'var(--text-light)' }}>Vayu Assistant</span>
          <span className="w-2 h-2 rounded-full" style={{ background: 'var(--success)' }} />
        </div>
        <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white/5" data-testid="chatbot-close">
          <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ maxHeight: 360 }}>
        {messages.map((msg, i) => <ChatMessage key={`${msg.role}-${i}`} msg={msg} />)}
        {loading && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>
      <div className="px-4 py-3" style={{ borderTop: '1px solid var(--border-dark)' }}>
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask about Vayu..." className="flex-1 px-4 py-2.5 rounded-xl text-sm" style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-dark)', color: 'var(--text-light)' }} data-testid="chatbot-input" />
          <button onClick={handleSend} disabled={loading || !input.trim()} className="w-10 h-10 rounded-xl flex items-center justify-center transition-all" style={{ background: input.trim() ? 'var(--aqua)' : 'rgba(63,167,214,0.2)' }} data-testid="chatbot-send">
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
